import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FileEdit, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  useCreateDraft,
  useEopAssignableProjects,
} from "@/features/eop_mail/api/mailbox";
import { extractError } from "./sanitize";
import { RefineButton } from "./RefineButton";

/**
 * Nowa wiadomość z eop@ — do KOGOKOLWIEK w sprawie przedsięwzięcia: operatora,
 * urzędu, wykonawcy, właściciela działki.
 *
 * Panel boczny na całą wysokość (wzorzec z TaskDetailPanel), nie modal na środku:
 * pisanie maila to praca z treścią obok kontekstu — człowiek chce widzieć kartę
 * projektu, z której pisze, a edytor ma rosnąć z ekranem, nie tonąć w okienku.
 *
 * Kończy się KOPIĄ ROBOCZĄ, nie wysyłką — jak wszystko w tym module.
 */
export function NewMessageModal({
  open,
  onClose,
  defaultProject,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  defaultProject?: string;
  onCreated?: (draftId: string) => void;
}) {
  const { data: projects } = useEopAssignableProjects();
  const createDraft = useCreateDraft();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [project, setProject] = useState(defaultProject ?? "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const parse = (value: string) =>
    value
      .split(/[,;\s]+/)
      .map((v) => v.trim())
      .filter(Boolean);

  const submit = () => {
    setError("");
    const recipients = parse(to);
    if (recipients.length === 0) {
      setError("Podaj co najmniej jednego adresata.");
      return;
    }
    if (!subject.trim()) {
      setError("Podaj temat — bez niego wątku nie da się później odnaleźć.");
      return;
    }
    createDraft.mutate(
      {
        subject: subject.trim(),
        body_html: body,
        to: recipients,
        cc: parse(cc),
        project: project || null,
      },
      {
        onSuccess: (draft) => {
          setTo("");
          setCc("");
          setSubject("");
          setBody("");
          onCreated?.(draft.id);
          onClose();
        },
        onError: (err) => setError(extractError(err)),
      },
    );
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[75] bg-black/30" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-[80] flex w-full max-w-2xl flex-col border-l border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
          <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            Nowa wiadomość z eop@
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nagłówki maila — zwarte, edytor dostaje całą resztę wysokości. */}
        <div className="shrink-0 space-y-2.5 border-b border-[var(--color-border)] px-5 py-3">
          <Input
            label="Do"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="adresat — ENERGA, urząd, wykonawca… (kilka adresów po przecinku)"
            autoFocus
          />
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            <Input
              label="DW (opcjonalnie)"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
            <SearchableSelect
              label="Przedsięwzięcie"
              options={(projects ?? []).map((p) => ({
                value: p.id,
                label: `${p.short_name} — ${p.name}${p.is_mine ? " (moje)" : ""}`,
              }))}
              placeholder="(opcjonalnie)"
              value={project}
              onChange={(e) => setProject(e.target.value)}
            />
          </div>
          <Input
            label="Temat"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        {/* Edytor wypełnia całą pozostałą wysokość panelu. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-3 [&_.rich-text-content]:min-h-[16rem]">
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Treść wiadomości. Podpis Twoim imieniem i nazwiskiem dokleja się automatycznie."
          />
        </div>

        <div className="shrink-0 border-t border-[var(--color-border)] px-5 py-3">
          {error && (
            <p className="mb-2 text-[11px] text-[var(--color-danger)]">{error}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={submit} loading={createDraft.isPending}>
              <FileEdit size={13} className="mr-1" />
              Zapisz kopię roboczą
            </Button>
            <RefineButton body={body} onRefined={setBody} />
            <Button variant="ghost" onClick={onClose}>
              Anuluj
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
            Zapis tworzy kopię roboczą w skrzynce eop@ (widoczną też w Outlooku).
            Nic nie wychodzi, dopóki ktoś nie kliknie „Wyślij”.
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
}
